import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './EditProjectBudget.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Select from 'react-select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import makeAnimated from "react-select/animated";
import { NumericFormat } from "react-number-format";

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}.${month}.${year}`;
};

function EditProjectBudget() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const animatedComponents = makeAnimated();

  const [formData, setFormData] = useState({
    offerCode: '',
    costId: '',
    costDescription: '',
    source_type: '',
    external_supplier: null,
    internal_supplier: null,
    material_supplier: null,
    number_of_hours: "",
    number_offer_without_vat: "",
    cost_offer_without_vat: "",
    cost_po_without_vat: "",
    price_per_hour: "",
    price_per_hour_value: "",
    currency: "",
    exchange_rate: "",
    selectedExchangeRate: null,
    cost_offer_without_vat_system: "",
    cost_po_without_vat_system: "",
    revenue_without_vat: "",
    revenue_po_without_vat: "",
  });

  const [offerCodes, setOfferCodes] = useState([]);
  const [errors, setErrors] = useState({});
  const [externalSuppliers, setExternalSuppliers] = useState([]);
  const [internalSuppliers, setInternalSuppliers] = useState([]);
  const [allCost, setAllCost] = useState([]);
  const [filteredCosts, setFilteredCosts] = useState([]);
  const [pricePerHourList, setPricePerHourList] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const getExchangeOptionsForCurrency = useCallback((currencyId) => {
    return exchangeRates
      .filter(rate => Number(rate.currency) === Number(currencyId))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(rate => ({
        value: rate.id,
        label: String(rate.value ?? ""),
        rateValue: Number(rate.value),
        date: rate.date,
      }));
  }, [exchangeRates]);

  const calculateSystemCost = useCallback((cost, currencyId, selectedExchangeRate) => {
    if (cost === "" || cost === null || cost === undefined) return "";

    const numericCost = Number(cost);
    if (!Number.isFinite(numericCost)) return "";

    if (!currencyId || !systemCurrencyId) return "";

    if (Number(currencyId) === Number(systemCurrencyId)) {
      return numericCost.toFixed(2);
    }

    if (!selectedExchangeRate?.rateValue) return "";

    return (numericCost * selectedExchangeRate.rateValue).toFixed(2);
  }, [systemCurrencyId]);

  const refreshSystemAmounts = useCallback((data) => ({
    ...data,
    cost_offer_without_vat_system: calculateSystemCost(
      data.cost_offer_without_vat,
      data.currency,
      data.selectedExchangeRate
    ),
    cost_po_without_vat_system: calculateSystemCost(
      data.cost_po_without_vat,
      data.currency,
      data.selectedExchangeRate
    ),
  }), [calculateSystemCost]);

  useEffect(() => {
    if (formData.source_type !== 'Internal') return;

    const calculatedCost =
      formData.number_of_hours && formData.price_per_hour_value
        ? (Number(formData.number_of_hours) * Number(formData.price_per_hour_value)).toFixed(2)
        : "";

    const calculatedCostPO =
      formData.number_offer_without_vat && formData.price_per_hour_value
        ? (Number(formData.number_offer_without_vat) * Number(formData.price_per_hour_value)).toFixed(2)
        : "";

    setFormData(prev =>
      refreshSystemAmounts({
        ...prev,
        cost_offer_without_vat: calculatedCost,
        cost_po_without_vat: calculatedCostPO,
      })
    );
  }, [
    formData.number_of_hours,
    formData.number_offer_without_vat,
    formData.price_per_hour_value,
    formData.source_type,
    formData.currency,
    formData.selectedExchangeRate,
    systemCurrencyId,
    refreshSystemAmounts
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!state?.id) {
          navigate('/project-budget');
          return;
        }

        const [
          codesResponse,
          costsResponse,
          currenciesResponse,
          exchangeRatesResponse,
          systemCurrencyResponse,
          resourcesResponse,
          pricesResponse,
          budgetResponse,
          departmentResponse,
          statusSupplierDeptResponse,
          suppliersResponse,
        ] = await Promise.all([
          axiosInstance.get('/offer-codes/'),
          axiosInstance.get('/budget-structure/'),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
          axiosInstance.get('/internal-resources/'),
          axiosInstance.get("/price-per-hour/"),
          axiosInstance.get(`/project-budget/${state.id}/`),
          axiosInstance.get("/department/user/"),
          axiosInstance.get("/status-supplier-department/"),
          axiosInstance.get("/suppliers/"),
        ]);

        const formattedCodes = codesResponse.data.map(code => ({
          value: code.id,
          label: `${code.offer_code} - ${code.description}`,
          offer_code: code.offer_code
        }));
        setOfferCodes(formattedCodes);

        const formattedCosts = costsResponse.data.map(dl => ({
          value: dl.id,
          label: `${dl.cost_element_id} - ${dl.cost_element_description}`,
          cost_element_id: dl.cost_element_id,
          cost_element_description: dl.cost_element_description,
          offer_code_id: dl.offer_code_details?.id
        }));
        setAllCost(formattedCosts);
        setFilteredCosts(formattedCosts);

        const formattedCurrencies = currenciesResponse.data.map(currency => ({
          value: currency.id,
          label: currency.abbreviation
        }));
        setCurrencyOptions(formattedCurrencies);

        const loadedExchangeRates = exchangeRatesResponse.data || [];
        setExchangeRates(loadedExchangeRates);

        const loadedSystemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) && systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency || null
            : null;

        setSystemCurrencyId(loadedSystemCurrencyId);

        const systemCurrency = formattedCurrencies.find(
          currency => Number(currency.value) === Number(loadedSystemCurrencyId)
        );
        setSystemCurrencyLabel(systemCurrency?.label || "");

        const activeInternalResources = resourcesResponse.data.filter(ir => ir.status === 'Activ');
        const formattedInternalResources = activeInternalResources.map(ir => ({
          value: ir.id,
          label: ir.resource_name
        }));
        setInternalSuppliers(formattedInternalResources);

        const pricesData = pricesResponse.data.map(item => ({
          value: item.id,
          label: `${item.price_per_hour} – ${item.date} – ${item.internal_resource_name}`,
          priceValue: item.price_per_hour,
          internalResourceId: item.internal_resource
        }));
        setPricePerHourList(pricesData);

        const record = budgetResponse.data;

        const accessibleDepartments = departmentResponse.data.map(dep => dep.value);
        const supplierDepartments = statusSupplierDeptResponse.data;

        const allowedSupplierIds = supplierDepartments
          .filter(item => item.status === "Active" && accessibleDepartments.includes(item.department))
          .map(item => item.supplier);

        let filteredSuppliers = suppliersResponse.data.filter(supplier =>
          allowedSupplierIds.includes(supplier.id)
        );

        const currentSupplier = record.supplier
          ? suppliersResponse.data.find(s => s.id === record.supplier)
          : null;

        if (currentSupplier && !filteredSuppliers.some(s => s.id === currentSupplier.id)) {
          filteredSuppliers = [currentSupplier, ...filteredSuppliers];
        }

        const supplierOptions = filteredSuppliers.map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }));
        setExternalSuppliers(supplierOptions);

        const budgetStructureRecord = formattedCosts.find(d => d.value === record.cost_element);

        const selectedOffer = formattedCodes.find(code =>
          code.offer_code === record.offer_code || code.value === Number(record.offer_code)
        );

        const offerCodeId = selectedOffer?.value || (budgetStructureRecord?.offer_code_id ?? null);

        if (offerCodeId) {
          setFilteredCosts(formattedCosts.filter(d => d.offer_code_id === offerCodeId));
        }

        const selectedPriceOption =
          pricesData.find(option => Number(option.priceValue) === Number(record.price_per_hour)) || null;

        const selectedExchangeRate = record.exchange_rate
          ? loadedExchangeRates
              .filter(rate => Number(rate.currency) === Number(record.currency))
              .map(rate => ({
                value: rate.id,
                label: String(rate.value ?? ""),
                rateValue: Number(rate.value),
                date: rate.date,
              }))
              .find(rate => Number(rate.value) === Number(record.exchange_rate)) || null
          : null;

        const initialData = {
          offerCode: selectedOffer
            ? selectedOffer.value
            : (budgetStructureRecord ? budgetStructureRecord.offer_code_id : ''),
          costId: record.cost_element || '',
          costDescription: record.cost_element || '',
          source_type: record.source_type || '',
          external_supplier: currentSupplier ? currentSupplier.id : null,
          internal_supplier: record.internal_resource || null,
          material_supplier: currentSupplier ? currentSupplier.id : null,
          number_of_hours: record.number_of_hours || "",
          number_offer_without_vat: record.number_offer_without_vat || "",
          cost_offer_without_vat: record.cost_offer_without_vat || "",
          cost_po_without_vat: record.cost_po_without_vat || "",
          price_per_hour: selectedPriceOption?.value || "",
          price_per_hour_value: selectedPriceOption?.priceValue || record.price_per_hour || "",
          currency: record.currency || "",
          exchange_rate: record.exchange_rate || "",
          selectedExchangeRate,
          cost_offer_without_vat_system: record.cost_offer_without_vat_system || "",
          cost_po_without_vat_system: record.cost_po_without_vat_system || "",
          revenue_without_vat: record.revenue_without_vat || "",
          revenue_po_without_vat: record.revenue_po_without_vat || "",
          supplier: currentSupplier ? currentSupplier.id : null
        };

        setFormData(refreshSystemAmounts(initialData));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [state?.id, navigate, refreshSystemAmounts]);

  const getFilteredPricePerHour = () => {
    if (!formData.internal_supplier) return [];
    return pricePerHourList.filter(price =>
      Number(price.internalResourceId) === Number(formData.internal_supplier)
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev =>
      refreshSystemAmounts({
        ...prev,
        [name]: value
      })
    );
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : null;

    if (name === 'offerCode') {
      setFormData(prev => {
        let nextData = {
          ...prev,
          offerCode: value,
        };

        if (selectedOption) {
          const filtered = allCost.filter(d => d.offer_code_id === value);
          setFilteredCosts(filtered);

          if (prev.costDescription && !filtered.some(d => d.value === prev.costDescription)) {
            nextData.costDescription = '';
            nextData.costId = '';
          }
        } else {
          setFilteredCosts(allCost);
        }

        return nextData;
      });

      return;
    }

    if (name === 'costDescription') {
      const selectedCost = allCost.find(d => Number(d.value) === Number(value));

      setFormData(prev => ({
        ...prev,
        costDescription: value || "",
        costId: selectedCost?.cost_element_id || "",
      }));

      return;
    }

    if (name === 'internal_supplier') {
      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          internal_supplier: value,
          price_per_hour: "",
          price_per_hour_value: "",
          cost_offer_without_vat: "",
          cost_po_without_vat: "",
        })
      );

      return;
    }

    if (name === 'price_per_hour') {
      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          price_per_hour: value || "",
          price_per_hour_value: selectedOption ? selectedOption.priceValue : "",
        })
      );

      return;
    }

    if (name === 'currency') {
      const isSystemCurrency =
        systemCurrencyId && Number(value) === Number(systemCurrencyId);

      const defaultExchangeRate = !isSystemCurrency && value
        ? getExchangeOptionsForCurrency(value)[0] || null
        : null;

      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          currency: value || "",
          exchange_rate: defaultExchangeRate?.value || "",
          selectedExchangeRate: defaultExchangeRate,
        })
      );

      return;
    }

    if (name === 'exchange_rate') {
      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          exchange_rate: value || "",
          selectedExchangeRate: selectedOption || null,
        })
      );

      return;
    }

    setFormData(prev =>
      refreshSystemAmounts({
        ...prev,
        [name]: value
      })
    );
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.offerCode) newErrors.offerCode = 'Offer code is required.';
    if (!formData.costDescription) newErrors.costDescription = 'Cost description is required.';
    if (!formData.source_type) newErrors.source_type = 'Source type is required.';
    if (!formData.cost_offer_without_vat) newErrors.cost_offer_without_vat = 'Cost without VAT is required.';
    if (!formData.currency) newErrors.currency = 'Currency is required.';

    const selectedCurrencyIsSystem =
      formData.currency &&
      systemCurrencyId &&
      Number(formData.currency) === Number(systemCurrencyId);

    if (formData.currency && !selectedCurrencyIsSystem && !formData.exchange_rate) {
      newErrors.exchange_rate = 'Exchange rate is required.';
    }

    if (formData.source_type === 'Internal' && !formData.internal_supplier) {
      newErrors.internal_supplier = 'Internal supplier is required.';
    }

    if (formData.source_type === 'External' && !formData.external_supplier) {
      newErrors.external_supplier = 'External supplier is required.';
    }

    if (formData.source_type === 'Material' && !formData.material_supplier) {
      newErrors.material_supplier = 'Material supplier is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        cost_element: Number(formData.costDescription),
        source_type: formData.source_type,

        supplier:
          formData.source_type === 'External'
            ? formData.external_supplier
            : formData.source_type === 'Material'
              ? formData.material_supplier
              : null,

        internal_resource:
          formData.source_type === 'Internal'
            ? formData.internal_supplier
            : null,

        number_of_hours:
          formData.source_type === 'Internal'
            ? parseFloat(formData.number_of_hours) || 0
            : null,

        number_offer_without_vat: parseFloat(formData.number_offer_without_vat) || null,
        cost_offer_without_vat: parseFloat(formData.cost_offer_without_vat) || 0,
        cost_po_without_vat: parseFloat(formData.cost_po_without_vat) || null,

        price_per_hour:
          formData.source_type === 'Internal'
            ? parseFloat(formData.price_per_hour_value) || null
            : null,

        revenue_without_vat: parseFloat(formData.revenue_without_vat) || 0,
        revenue_po_without_vat: parseFloat(formData.revenue_po_without_vat) || 0,

        currency: formData.currency ? Number(formData.currency) : null,

        exchange_rate:
          formData.exchange_rate && Number(formData.currency) !== Number(systemCurrencyId)
            ? Number(formData.exchange_rate)
            : null,
      };

      await axiosInstance.put(`/project-budget/${state.id}/`, payload);
      navigate('/project-budget');
    } catch (error) {
      console.error('Error saving data:', error.response?.data || error.message);
      alert(`Eroare la salvare: ${JSON.stringify(error.response?.data || error.message)}`);
    }
  };

  const handleDiscard = () => navigate('/project-budget');
  const handleUndo = () => navigate(-1);

  const selectedCurrencyIsSystem =
    formData.currency &&
    systemCurrencyId &&
    Number(formData.currency) === Number(systemCurrencyId);

  const exchangeOptions = formData.currency
    ? getExchangeOptionsForCurrency(formData.currency)
    : [];

  return (
    <div className="edit-project-budget-page">
      <div className="edit-container">
        <div className="header">
          <IconButton onClick={handleUndo}>
            <ArrowBackIcon className="back-arrow" />
          </IconButton>
          <h2 className="edit-title">Edit budget</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <div className="edit-container">
        <form onSubmit={handleSave} className="edit-form">
          <div className="form-group">
            <label htmlFor="offer_code" className="label">
              Offer Code <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="offer_code"
              name="offerCode"
              classNamePrefix="react-select"
              options={offerCodes}
              value={offerCodes.find(o =>
                o.value === Number(formData.offerCode) ||
                o.offer_code === formData.offerCode
              ) || null}
              onChange={selected => handleSelectChange('offerCode', selected)}
              isClearable
              styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
            />
            {errors.offerCode && <div className="error">{errors.offerCode}</div>}
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="cost_element_description" className="label">
                Cost Description <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="cost_element_description"
                name="costDescription"
                classNamePrefix="react-select"
                options={filteredCosts}
                value={filteredCosts.find(d => Number(d.value) === Number(formData.costDescription)) || null}
                onChange={selected => handleSelectChange('costDescription', selected)}
                isClearable
                styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
              />
              {errors.costDescription && <div className="error">{errors.costDescription}</div>}
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="source_type" className="label">
                Source Type <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="source_type"
                name="source_type"
                value={formData.source_type}
                className="input"
                disabled
                readOnly
              />
            </div>

            {formData.source_type === 'External' && (
              <div className="form-group">
                <label htmlFor="external_supplier" className="label">
                  External Supplier <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  id="external_supplier"
                  name="external_supplier"
                  classNamePrefix="react-select"
                  options={externalSuppliers}
                  value={externalSuppliers.find(s => s.value === Number(formData.external_supplier)) || null}
                  onChange={selected => handleSelectChange('external_supplier', selected)}
                  components={animatedComponents}
                  isClearable
                  styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
                />
                {errors.external_supplier && <div className="error">{errors.external_supplier}</div>}
              </div>
            )}

            {formData.source_type === 'Internal' && (
              <div className="form-group">
                <label htmlFor="internal_supplier" className="label">
                  Internal Supplier <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  id="internal_supplier"
                  name="internal_supplier"
                  classNamePrefix="react-select"
                  options={internalSuppliers}
                  value={internalSuppliers.find(s => s.value === Number(formData.internal_supplier)) || null}
                  onChange={selected => handleSelectChange('internal_supplier', selected)}
                  components={animatedComponents}
                  isClearable
                  styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
                />
                {errors.internal_supplier && <div className="error">{errors.internal_supplier}</div>}
              </div>
            )}

            {formData.source_type === 'Material' && (
              <div className="form-group">
                <label htmlFor="material_supplier" className="label">
                  Material Supplier <span style={{ color: "red" }}>*</span>
                </label>
                <Select
                  id="material_supplier"
                  name="material_supplier"
                  classNamePrefix="react-select"
                  options={externalSuppliers}
                  value={externalSuppliers.find(s => s.value === Number(formData.material_supplier)) || null}
                  onChange={selected => handleSelectChange('material_supplier', selected)}
                  components={animatedComponents}
                  isClearable
                  styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
                />
                {errors.material_supplier && <div className="error">{errors.material_supplier}</div>}
              </div>
            )}
          </div>

          {formData.source_type === 'Internal' && (
            <div className="form-group-row">
              <div className="form-group">
                <label htmlFor="number_of_hours" className="label">
                  Nr. of Hours <span style={{ color: 'red' }}>*</span>
                </label>
                <NumericFormat
                  id="number_of_hours"
                  name="number_of_hours"
                  value={formData.number_of_hours}
                  onValueChange={({ value }) =>
                    handleChange({
                      target: { name: "number_of_hours", value: value || "" },
                    })
                  }
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className="input"
                  placeholder="0,00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="number_offer_without_vat" className="label">
                  Nr. of Hours PO
                </label>
                <NumericFormat
                  id="number_offer_without_vat"
                  name="number_offer_without_vat"
                  value={formData.number_offer_without_vat}
                  onValueChange={({ value }) =>
                    handleChange({
                      target: {
                        name: "number_offer_without_vat",
                        value: value || "",
                      },
                    })
                  }
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className="input"
                  placeholder="0,00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="price_per_hour" className="label">
                  Price per Hour <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  id="price_per_hour"
                  name="price_per_hour"
                  classNamePrefix="react-select"
                  options={getFilteredPricePerHour()}
                  value={getFilteredPricePerHour().find(option =>
                    option.value === Number(formData.price_per_hour)
                  ) || null}
                  onChange={(selectedOption) =>
                    handleSelectChange('price_per_hour', selectedOption)
                  }
                  isClearable
                  styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
                />
              </div>
            </div>
          )}

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="cost_offer_without_vat" className="label">
                Cost without VAT <span style={{ color: 'red' }}>*</span>
              </label>
              <NumericFormat
                id="cost_offer_without_vat"
                name="cost_offer_without_vat"
                value={formData.cost_offer_without_vat}
                onValueChange={({ value }) =>
                  handleChange({
                    target: {
                      name: "cost_offer_without_vat",
                      value: value || "",
                    },
                  })
                }
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly={formData.source_type === "Internal"}
                required
              />
              {errors.cost_offer_without_vat && <div className="error">{errors.cost_offer_without_vat}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="cost_po_without_vat" className="label">
                Cost PO without VAT
              </label>
              <NumericFormat
                id="cost_po_without_vat"
                name="cost_po_without_vat"
                value={formData.cost_po_without_vat}
                onValueChange={({ value }) =>
                  handleChange({
                    target: {
                      name: "cost_po_without_vat",
                      value: value || "",
                    },
                  })
                }
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly={formData.source_type === "Internal"}
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="label">
                Currency <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="currency"
                name="currency"
                classNamePrefix="react-select"
                options={currencyOptions}
                value={currencyOptions.find(s => Number(s.value) === Number(formData.currency)) || null}
                onChange={selected => handleSelectChange('currency', selected)}
                isClearable
                styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
              />
              {errors.currency && <div className="error">{errors.currency}</div>}
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="exchange_rate" className="label">
                Exchange Rate
                {!selectedCurrencyIsSystem && formData.currency ? (
                  <span style={{ color: 'red' }}> *</span>
                ) : null}
              </label>
              <Select
                id="exchange_rate"
                name="exchange_rate"
                classNamePrefix="react-select"
                options={exchangeOptions}
                value={formData.selectedExchangeRate}
                onChange={selected => handleSelectChange('exchange_rate', selected)}
                components={animatedComponents}
                isClearable
                isDisabled={!formData.currency || selectedCurrencyIsSystem}
                placeholder={
                  !formData.currency
                    ? "Select currency first"
                    : selectedCurrencyIsSystem
                      ? "Not needed"
                      : exchangeOptions.length
                        ? "Select rate"
                        : "No rates"
                }
                formatOptionLabel={(option, { context }) =>
                  context === "menu"
                    ? `${option.label}${option.date ? ` - ${formatDateLabel(option.date)}` : ""}`
                    : option.label
                }
                styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
              />
              {errors.exchange_rate && <div className="error">{errors.exchange_rate}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="cost_offer_without_vat_system" className="label">
                Cost without VAT System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
              </label>
              <NumericFormat
                id="cost_offer_without_vat_system"
                name="cost_offer_without_vat_system"
                value={formData.cost_offer_without_vat_system}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="cost_po_without_vat_system" className="label">
                Cost PO without VAT System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
              </label>
              <NumericFormat
                id="cost_po_without_vat_system"
                name="cost_po_without_vat_system"
                value={formData.cost_po_without_vat_system}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="button-group-inline">
              <button type="button" className="button discard" onClick={handleDiscard}>
                Discard
              </button>
              <button type="submit" className="button save">
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProjectBudget;